/**
 * Proof that a Stripe round trip is one we started.
 *
 * The booking id on the cancel URL is only ever a claim — it is whatever sits in the address bar.
 * Acting on it unconditionally means a link someone else sends ("/my-bookings?cancelled=216")
 * makes the signed-in coach's own browser release a live hold on page load, with the app's own
 * antiforgery token attached. So we record the id when we hand off to Stripe and only release a
 * hold when the return matches. Same-tab redirects keep sessionStorage, so the happy path is
 * untouched; a link from outside carries no matching entry.
 */
const CHECKOUT_HANDOFF_KEY = 'checkout-handoff-booking';

/**
 * A handoff outlives its usefulness the moment the hold it belongs to lapses. Without this a
 * coach who left Stripe by any route other than the two redirects — browser Back, closing the
 * tab's Stripe page and navigating in-app — would carry a live marker for the rest of the tab's
 * life, and booking ids are small sequential integers, so a crafted return could eventually be
 * made to match one. Comfortably past `Booking_HoldMinutes` (15), so it never truncates a real
 * checkout; the server re-reads Stripe regardless.
 */
const HANDOFF_TTL_MS = 20 * 60 * 1000;

interface Handoff {
  id: number;
  ts: number;
}

/** Called as we send the coach to Stripe, just before the redirect. */
export function recordCheckoutHandoff(bookingId: number) {
  const handoff: Handoff = { id: bookingId, ts: Date.now() };
  sessionStorage.setItem(CHECKOUT_HANDOFF_KEY, JSON.stringify(handoff));
}

/**
 * Payment landed, the handoff is spent, or the coach came back to the app some other way — the
 * marker must not outlive the round trip it belongs to.
 */
export const clearCheckoutHandoff = () => sessionStorage.removeItem(CHECKOUT_HANDOFF_KEY);

/** null for anything unreadable, so a hand-edited entry cannot be made to match. */
function readCheckoutHandoff(): Handoff | null {
  const raw = sessionStorage.getItem(CHECKOUT_HANDOFF_KEY);
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const { id, ts } = parsed as Partial<Handoff>;
    if (!Number.isInteger(id) || !Number.isFinite(ts)) return null;
    return { id: id as number, ts: ts as number };
  } catch {
    return null;
  }
}

/**
 * The booking id a LIVE handoff points at, without spending it — the paid-success landing uses
 * this to know which booking to reconcile with Stripe (the success URL deliberately carries no
 * id it would have to trust). Same TTL rule as consuming: an expired marker identifies nothing.
 */
export function peekCheckoutHandoff(): number | null {
  const handoff = readCheckoutHandoff();
  if (!handoff || Date.now() - handoff.ts > HANDOFF_TTL_MS) return null;
  return handoff.id;
}

/**
 * The just-paid booking, while its confirmation is still being reconciled with Stripe. Written
 * at the paid-success landing so the OTHER payment surfaces (the after-login nudge, My payments)
 * stop offering "Pay now" for a booking whose money has already been taken — the full page
 * reload on the redirect means React state cannot carry this across components. Short TTL: if
 * confirmation somehow never lands, the row comes back rather than being silently unpayable.
 */
const CONFIRMING_KEY = 'payment-confirming-booking';
const CONFIRMING_TTL_MS = 2 * 60 * 1000;

export function markPaymentConfirming(bookingId: number) {
  sessionStorage.setItem(CONFIRMING_KEY, JSON.stringify({ id: bookingId, ts: Date.now() }));
}

export const clearPaymentConfirming = () => sessionStorage.removeItem(CONFIRMING_KEY);

/** The booking id being confirmed, or null once cleared/expired/unreadable. */
export function paymentConfirmingId(): number | null {
  const raw = sessionStorage.getItem(CONFIRMING_KEY);
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const { id, ts } = parsed as Partial<Handoff>;
    if (!Number.isInteger(id) || !Number.isFinite(ts)) return null;
    if (Date.now() - (ts as number) > CONFIRMING_TTL_MS) return null;
    return id as number;
  } catch {
    return null;
  }
}

/**
 * The booking id to release after a cancelled checkout, or null when this return cannot be
 * matched to a live handoff we made — an unusable id, a redirect this tab never started, a
 * handoff older than the hold it belongs to, or the legacy allocation-level series checkout,
 * whose cancel URL is "cancelled=series" rather than an id (per-week blocks pay through the
 * ordinary single-booking checkout, so those DO return a real id; releasing one releases the
 * whole series server-side, since every week shares one hold). Single-use: the marker is spent
 * either way, so a replayed URL releases nothing.
 */
export function consumeCheckoutHandoff(cancelled: string | null): number | null {
  const handoff = readCheckoutHandoff();
  clearCheckoutHandoff();
  if (!cancelled || !handoff) return null;
  if (Date.now() - handoff.ts > HANDOFF_TTL_MS) return null;
  const bookingId = Number(cancelled);
  if (!Number.isInteger(bookingId) || bookingId <= 0) return null;
  return bookingId === handoff.id ? bookingId : null;
}
